import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Divider, FormControlLabel, Paper, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { RootState } from "../../../../redux/store";
import EnergySavingsSlidingMenuMobile from "../EnergySavingsSlidingMenuMobile";
import EnergyDataChart from "../EnergyDataChart";
import { editSite } from "../../../../services/apis";

const leftCardStyles = {
  boxShadow: "0px 1px 14px 0px #00071624",
  padding: "20px",
};

const getRightCardStyles = (isSmallScreen: boolean) => ({
  height: "227px",
  boxShadow: "0px 1px 14px 0px #00071624",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  width: isSmallScreen ? "50%" : "100%",
});

const titleStyles = {
  fontSize: "40px",
  fontWeight: "700",
  color: "primary.main",
};

const subtitleStyles = {
  fontSize: "14px",
  fontWeight: "700",
  color: "text.secondary",
  textTransform: "uppercase",
};

export default function EnergyDataCardSite({ projectName, siteName, setEnableSearchQuery, userRole }: any) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const siteData = useSelector((state: RootState) => state.siteDirectory.sitesData);
  // State for energy toggle
  const [energyType, setEnergyType] = useState("KWH");
  const [loadingForDR, setLoadingForDR] = useState(false);
  const [energySavingsTracked, setEnergySavingsTracked] = useState<number>(0);
  const [carbonReductionSavingsTracked, setCarbonReductionSavingsTracked] = useState<number>(0);

  const cleanPayload = (payload: Record<string, any>) => {
    return Object.fromEntries(Object.entries(payload).filter(([_, value]) => value !== ""));
  };

  // Update values when energyType or siteData changes
  useEffect(() => {
    const rawCarbonReductionSavings = parseFloat(String(siteData?.energy_savings_data?.carbon_reduction_savings)) || 0;

    const rawEnergySavings = parseFloat(String(siteData?.energy_savings_data?.total_savings)) || 0;
    const rawEnergySavingsTherms = parseFloat(String(siteData?.energy_savings_data?.total_savings_therms)) || 0;

    // Ensure values have at most 2 decimal places and remain numbers
    const formattedCarbonReductionSavings = parseFloat(rawCarbonReductionSavings.toFixed(2));
    const formattedEnergySavings = parseFloat(rawEnergySavings.toFixed(2));
    const formattedEnergySavingsTherms = parseFloat(rawEnergySavingsTherms.toFixed(2));

    setCarbonReductionSavingsTracked(formattedCarbonReductionSavings);
    setEnergySavingsTracked(energyType === "THERMS" ? formattedEnergySavingsTherms : formattedEnergySavings);
  }, [energyType, siteData]);

  // Debugging: Log changes
  // useEffect(() => {
  //   console.log("Updated values:", { carbonReductionSavingsTracked, energySavingsTracked, energyType });
  // }, [carbonReductionSavingsTracked, energySavingsTracked, energyType]);

  const handleDrChange = () => {
    setLoadingForDR(true);
    editSiteMutation.mutate(
      cleanPayload({
        site_id: siteData?.site_data?.site_id,
        name: siteData?.site_data?.site_name,
        country: siteData?.site_data?.country,
        address_line1: siteData?.site_data?.address_line1,
        address_line2: siteData?.site_data?.address_line2 === "-" ? "" : siteData?.site_data?.address_line2,
        city: siteData?.site_data?.city,
        state: siteData?.site_data?.state,
        postal_code: siteData?.site_data?.postal_code,
        phone_number: siteData?.site_data?.phone_number,
        dr_event_enrolled: !siteData?.units_dr_enrolled,
        project_id: Number(siteData?.project_data?.project_id),
      })
    );
  };

  const editSiteMutation = useMutation({
    mutationFn: editSite,
    onSuccess: (data) => {
      console.log("Site edited successfully:", data);
      queryClient.removeQueries({ queryKey: ["siteDashboardData"] });
      setLoadingForDR(false);
      setEnableSearchQuery(true);
    },
    onError: (error) => {
      setLoadingForDR(false);
      console.error("Error creating project:", error);
    },
  });

  const handleChangeEnergyType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnergyType(event.target.checked ? "THERMS" : "KWH"); // Directly update state
  };

  const returnKpiCards = () => (
    <Grid size={{ xs: 12, lg: 2 }}>
      <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "row" : "column" }}>
        <Paper sx={getRightCardStyles(isSmallScreen)}>
          <Typography sx={titleStyles}>
            {Number(siteData?.electric_demand_last_15_min || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          <Typography sx={subtitleStyles} align="center">
            electric demand
            <br /> last 15 min (kw)
          </Typography>
        </Paper>
        <Box sx={{ marginTop: isSmallScreen ? "0px" : "20px", marginLeft: isSmallScreen ? "20px" : "0px" }} />
        <Paper sx={getRightCardStyles(isSmallScreen)}>
          {loadingForDR ? (
            <CircularProgress />
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <Typography variant="body1">Off</Typography>
                <Switch
                  sx={{ "& .MuiSwitch-thumb": { color: siteData?.dr_event_enrolled ? "#193561" : "white" } }}
                  checked={siteData?.units_dr_enrolled === -1 || siteData?.units_dr_enrolled === 1}
                  onChange={handleDrChange}
                  disabled={siteData?.dr_event_enrolled === null || userRole == "TECHNICIAN"}
                />
                <Typography variant="body1">On</Typography>
              </Box>
              {siteData?.units_dr_enrolled === 1 ? (
                <CheckCircleIcon sx={{ width: 80, height: 80, marginTop: "10px", color: "#2E7D32" }} />
              ) : siteData?.units_dr_enrolled === -1 ? (
                <RemoveCircleIcon sx={{ width: 80, height: 80, marginTop: "10px", color: "#0288D1" }} />
              ) : (
                <CancelIcon sx={{ width: 80, height: 80, marginTop: "10px", color: "#D32F2F" }} />
              )}
              <Typography sx={{ color: "gray", textAlign: "center" }} variant="h5">
                demand response
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Grid>
  );

  const returnChart = (energySavings: any, carbonReductionSavings: any) => (
    <Grid size={{ xs: 12, lg: 10 }}>
      <Paper sx={leftCardStyles}>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h3">year to date</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1">{"kWh"}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={energyType === "THERMS"}
                  sx={{
                    "& .MuiSwitch-thumb": {
                      color: "#193561",
                    },
                  }}
                  onChange={handleChangeEnergyType}
                />
              }
              label="therms"
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", width: "100%" }}>
          {!isSmallScreen ? (
            <Grid container spacing={3} sx={{ marginTop: "10px", width: "100%" }}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box>
                  <Typography sx={titleStyles} align="center">
                    {Number(energySavingsTracked || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography sx={subtitleStyles} align="center">
                    energy savings
                  </Typography>
                </Box>
              </Grid>
              <Divider orientation="vertical" flexItem />
              <Grid size={{ xs: 12, md: 2 }}>
                <Box>
                  <Typography sx={titleStyles} align="center">
                    {carbonReductionSavingsTracked}
                  </Typography>
                  <Typography sx={subtitleStyles} align="center">
                    carbon reduction
                    <br />
                    savings (mTons)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <EnergySavingsSlidingMenuMobile energySavings={energySavings} carbonReductionSavings={carbonReductionSavings} />
          )}
        </Box>
        <Box sx={{ marginTop: "20px" }} />
        <EnergyDataChart kpiCardData={siteData} projectName={projectName} siteName={siteName} energyType={energyType} />
      </Paper>
    </Grid>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h2">Energy data</Typography>
      <Grid container spacing={3} sx={{ marginTop: "20px" }}>
        {isSmallScreen ? (
          <>
            {returnKpiCards()}
            {returnChart(energySavingsTracked, carbonReductionSavingsTracked)}
          </>
        ) : (
          <>
            {returnChart(energySavingsTracked, carbonReductionSavingsTracked)}
            {returnKpiCards()}
          </>
        )}
      </Grid>
    </Box>
  );
}
